import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import drawerClasses from './Drawer.module.scss';

function createPortalRoot(id) {
  const drawerRoot = document.createElement('div');
  drawerRoot.setAttribute('id', id);

  return drawerRoot;
}
const useMountTransition = (isMounted, unmountDelay) => {
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    let timeoutId;

    if (isMounted && !isTransitioning) {
      setIsTransitioning(true);
    } else if (!isMounted && isTransitioning) {
      timeoutId = setTimeout(() => setIsTransitioning(false), unmountDelay);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [unmountDelay, isMounted, isTransitioning]);

  return isTransitioning;
};

export default function Drawer({
  id = 'drawer-root',
  isOpen,
  children,
  className,
  onClose,
  position = 'left',
  removeWhenClosed = true,
}) {
  const bodyRef = useRef(document.querySelector('body'));
  const portalRootRef = useRef(
    document.getElementById(id) || createPortalRoot(id),
  );

  const isTransitioning = useMountTransition(isOpen, 300);
  useEffect(() => {
    const updatePageScroll = () => {
      if (isOpen) {
        bodyRef.current.style.overflow = 'hidden';
      } else {
        bodyRef.current.style.overflow = '';
      }
    };
    updatePageScroll();
  }, [isOpen]);

  useEffect(() => {
    bodyRef.current.appendChild(portalRootRef.current);
    const portal = portalRootRef.current;
    const bodyEl = bodyRef.current;
    return () => {
      portal.remove();
      bodyEl.style.overflow = '';
    };
  }, [portalRootRef]);

  useEffect(() => {
    const onKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keyup', onKeyPress);
    }

    return () => {
      window.removeEventListener('keyup', onKeyPress);
    };
  }, [isOpen, onClose]);

  if (!isTransitioning && removeWhenClosed && !isOpen) {
    return null;
  }

  return createPortal(
    <div
      aria-hidden={isOpen ? 'false' : 'true'}
      className={cn(drawerClasses.drawerContainer, {
        [drawerClasses.open]: isOpen,
        [drawerClasses.in]: isTransitioning,
      })}
    >
      <div
        className={cn(drawerClasses.drawer, drawerClasses[position], className)}
        role="dialog"
      >
        {children}
      </div>
      <div
        className={drawerClasses.backdrop}
        onClick={onClose}
        aria-hidden="true"
        role="button"
        tabIndex="0"
      />
    </div>,
    portalRootRef.current,
  );
}
Drawer.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  position: PropTypes.oneOf(['left', 'right']),
  removeWhenClosed: PropTypes.bool,
};
